import * as React from "react"
import { type QueryClient, useQueryClient } from "@tanstack/react-query"

import { useLocale } from "@/i18n"

/**
 * The language each query cache was last filled in. Kept per client, OUTSIDE the component: the provider tree
 * can remount on a language change (the first load renders the browser language, then the workspace language
 * takes over), and a ref would remount already holding the new language, so nothing would be refetched.
 */
const cacheLocales = new WeakMap<QueryClient, string>()

/**
 * ADR-0030: the API returns seeded names (chart of accounts, linked funds) already localized in the request's
 * language, so cached responses are in the language they were fetched in. When the UI language changes,
 * every query is invalidated (a fetch still in flight is refetched once it lands), and whatever is on screen
 * refetches with the new `X-Locale`.
 *
 * Invalidating all queries (rather than putting the locale in every query key) is deliberate: one call, nothing
 * a new hook can forget, and a language switch is rare.
 */
export function LocaleQuerySync() {
  const locale = useLocale()
  const queryClient = useQueryClient()

  React.useEffect(() => {
    const previous = cacheLocales.get(queryClient)
    cacheLocales.set(queryClient, locale)
    if (previous === undefined || previous === locale) return
    // A fetch still in flight was sent in the OLD language (the first load), and React Query dedupes a refetch
    // onto it. Never cancel it (that would drop data the app is waiting on, auth included): let it land, then
    // refetch that query once more in the new language.
    const cache = queryClient.getQueryCache()
    const pending = new Set(cache.findAll({ fetchStatus: "fetching" }).map((query) => query.queryHash))
    void queryClient.invalidateQueries()
    if (pending.size === 0) return
    // Not tied to this component's lifetime: the provider tree may remount right after a language change.
    const unsubscribe = cache.subscribe((event) => {
      const query = event.query
      if (!pending.has(query.queryHash)) return
      if (event.type !== "removed" && query.state.fetchStatus !== "idle") return
      pending.delete(query.queryHash)
      if (event.type !== "removed") void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true })
      if (pending.size === 0) unsubscribe()
    })
  }, [locale, queryClient])

  return null
}
